import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { toast } from "sonner";
import {
  Pencil,
  Trash2,
  Plus,
  Search,
  Upload,
  Image as ImageIcon,
  X,
} from "lucide-react";

/* =========================================================
   IMAGE UPLOAD
   ---------------------------------------------------------
   CHANGED: the /admin/upload endpoint now returns a
   { url, variants: { desktop, tablet, mobile, fallback } }
   payload instead of a bare URL string.

   `value` still stores just the desktop URL (a plain string),
   so anything reading this field elsewhere (table previews,
   old records without variants) keeps working unchanged.

   The variants object is stored in a *sibling* form field,
   `${field.key}_variants`, via the new `onVariantsChange`
   prop. AdminCrud wires this up automatically below - no
   changes needed in your resource field configs.
========================================================= */

function ImageUpload({
  value,
  onChange,
  variants,
  onVariantsChange,
  testId = "image-upload",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [value]);

  const upload = async (e) => {
    const file = e.target.files?.[0];

    // Reset input so the same file can be selected again
    e.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }

    // 10 MB limit
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be smaller than 10 MB.");
      return;
    }

    setBusy(true);

    try {
      const fd = new FormData();
      fd.append("file", file);

      /*
       * Do NOT manually set Content-Type here.
       * Axios/browser will automatically add the multipart boundary.
       */
      const response = await api.post("/admin/upload", fd);

      const uploadedUrl =
        response?.data?.url ||
        response?.data?.file_url ||
        response?.data?.path;

      if (!uploadedUrl) {
        throw new Error("Upload succeeded but no image URL was returned.");
      }

      onChange(uploadedUrl);

      // NEW: capture the responsive variants if the backend sent them.
      // Falls back gracefully to null if hitting an older/unmodified
      // upload endpoint, so this never breaks against a partial rollout.
      if (onVariantsChange) {
        onVariantsChange(response?.data?.variants || null);
      }

      setImageError(false);

      toast.success("Image uploaded successfully.");
    } catch (error) {
      console.error("Image upload error:", error);

      const message =
        formatApiError(error?.response?.data?.detail) ||
        formatApiError(error?.response?.data?.message) ||
        error?.response?.data?.error ||
        error?.message ||
        "Image upload failed.";

      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const removeImage = () => {
    onChange("");
    if (onVariantsChange) onVariantsChange(null);
    setImageError(false);
  };

  return (
    <div className="space-y-3">
      {/* URL + Upload */}
      <div className="flex items-center gap-2">
        <Input
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            // A manually pasted URL has no known variants - clear any
            // stale ones so the site doesn't serve mismatched images.
            if (onVariantsChange) onVariantsChange(null);
            setImageError(false);
          }}
          placeholder="Image URL"
          data-testid={`${testId}-url`}
          className="flex-1"
        />

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testId}-btn`}
          title="Upload image"
        >
          <Upload className="w-4 h-4" />
        </Button>

        <input
          ref={inputRef}
          type="file"
          hidden
          accept="image/*"
          onChange={upload}
        />
      </div>

      {/* Image preview */}
      {value && (
        <div className="relative w-fit">
          {!imageError ? (
            <img
              src={value}
              alt="Preview"
              className="h-28 w-44 rounded-lg border border-slate-200 object-cover bg-slate-100"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="h-28 w-44 rounded-lg border border-red-200 bg-red-50 flex flex-col items-center justify-center text-red-500 text-xs text-center p-2">
              <ImageIcon className="w-6 h-6 mb-1" />
              <span>Image could not be loaded</span>
            </div>
          )}

          <button
            type="button"
            onClick={removeImage}
            className="absolute -right-2 -top-2 w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center hover:bg-red-50"
            title="Remove image"
          >
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}

      {/* NEW: shows which responsive sizes were generated, for confidence */}
      {value && variants && (
        <div className="text-xs text-slate-400">
          Optimized for desktop, tablet &amp; mobile ✓
        </div>
      )}

      {/* Uploading */}
      {busy && (
        <div className="text-xs text-slate-500">
          Uploading and generating responsive sizes…
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ADMIN CRUD
========================================================= */

/**
 * Generic admin CRUD table.
 *
 * Supported field types:
 * text
 * textarea
 * number
 * switch
 * image
 * select
 * json
 * list
 *
 * NOTE on "image" fields: alongside `field.key` (the desktop URL,
 * a string, unchanged), a sibling key `${field.key}_variants`
 * is now saved on the record holding
 * { desktop, tablet, mobile, fallback } URLs when available.
 * Your backend model/schema for each resource should allow an
 * optional extra field of that name (or just accept arbitrary
 * extra fields) so it persists - it's harmless if ignored.
 */

export default function AdminCrud({
  title,
  resource,
  fields,
  defaultItem = {},
  extraColumns = [],
}) {
  const qc = useQueryClient();

  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...defaultItem });

  /* =======================================================
     LOAD DATA
  ======================================================= */

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [resource, q],

    queryFn: async () => {
      const response = await api.get(`/admin/${resource}`, {
        params: {
          search: q || undefined,
        },
      });

      return response.data;
    },
  });

  const items = data?.items || [];

  /* =======================================================
     FORM
  ======================================================= */

  const openCreate = () => {
    setEditing(null);

    /*
     * Clone the default object so arrays don't share references.
     */
    setForm({
      ...defaultItem,
      gallery: Array.isArray(defaultItem.gallery)
        ? [...defaultItem.gallery]
        : defaultItem.gallery,

      highlights: Array.isArray(defaultItem.highlights)
        ? [...defaultItem.highlights]
        : defaultItem.highlights,

      itinerary: Array.isArray(defaultItem.itinerary)
        ? [...defaultItem.itinerary]
        : defaultItem.itinerary,

      inclusions: Array.isArray(defaultItem.inclusions)
        ? [...defaultItem.inclusions]
        : defaultItem.inclusions,

      exclusions: Array.isArray(defaultItem.exclusions)
        ? [...defaultItem.exclusions]
        : defaultItem.exclusions,

      faqs: Array.isArray(defaultItem.faqs)
        ? [...defaultItem.faqs]
        : defaultItem.faqs,
    });

    setCreating(true);
  };

  const openEdit = (item) => {
    setCreating(false);

    setForm({
      ...item,

      gallery: Array.isArray(item.gallery)
        ? [...item.gallery]
        : item.gallery || [],

      highlights: Array.isArray(item.highlights)
        ? [...item.highlights]
        : item.highlights || [],

      itinerary: Array.isArray(item.itinerary)
        ? [...item.itinerary]
        : item.itinerary || [],

      inclusions: Array.isArray(item.inclusions)
        ? [...item.inclusions]
        : item.inclusions || [],

      exclusions: Array.isArray(item.exclusions)
        ? [...item.exclusions]
        : item.exclusions || [],

      faqs: Array.isArray(item.faqs)
        ? [...item.faqs]
        : item.faqs || [],
    });

    setEditing(item);
  };

  const close = () => {
    setCreating(false);
    setEditing(null);
    setForm({ ...defaultItem });
  };

  const setField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  /* =======================================================
     SAVE
  ======================================================= */

  const save = useMutation({
    mutationFn: async () => {
      /*
       * Convert JSON string fields back to objects before saving.
       */
      const payload = { ...form };

      fields.forEach((field) => {
        if (field.type === "json") {
          const value = payload[field.key];

          if (typeof value === "string") {
            try {
              payload[field.key] = JSON.parse(value);
            } catch {
              throw new Error(
                `${field.label} contains invalid JSON.`
              );
            }
          }
        }
      });

      if (editing) {
        const response = await api.put(
          `/admin/${resource}/${editing.id}`,
          payload
        );

        return response.data;
      }

      const response = await api.post(
        `/admin/${resource}`,
        payload
      );

      return response.data;
    },

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [resource],
      });

      toast.success(
        editing
          ? `${title.replace(/s$/, "")} updated successfully.`
          : `${title.replace(/s$/, "")} created successfully.`
      );

      close();
    },

    onError: (error) => {
      console.error("Save error:", error);

      const message =
        formatApiError(error?.response?.data?.detail) ||
        formatApiError(error?.response?.data?.message) ||
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong. Please try again.";

      toast.error(message);
    },
  });

  /* =======================================================
     DELETE
  ======================================================= */

  const del = useMutation({
    mutationFn: async (id) => {
      return api.delete(`/admin/${resource}/${id}`);
    },

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [resource],
      });

      toast.success("Deleted successfully.");
    },

    onError: (error) => {
      console.error("Delete error:", error);

      const message =
        formatApiError(error?.response?.data?.detail) ||
        formatApiError(error?.response?.data?.message) ||
        error?.message ||
        "Delete failed.";

      toast.error(message);
    },
  });

  /* =======================================================
     ACTIVE TOGGLE
  ======================================================= */

  const toggle = useMutation({
    mutationFn: async (id) => {
      return api.post(`/admin/${resource}/toggle/${id}`);
    },

    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [resource],
      });
    },

    onError: (error) => {
      console.error("Toggle error:", error);

      const message =
        formatApiError(error?.response?.data?.detail) ||
        formatApiError(error?.response?.data?.message) ||
        error?.message ||
        "Unable to change status.";

      toast.error(message);
    },
  });

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="space-y-6">
      {/* ===================================================
          HEADER
      =================================================== */}

      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.24em] text-amber-600">
            Manage
          </div>

          <h1 className="font-display text-3xl text-slate-950 mt-1">
            {title}
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="pl-9 w-64"
              data-testid={`${resource}-search`}
            />
          </div>

          {/* New */}
          <Button
            onClick={openCreate}
            className="bg-slate-950 hover:bg-slate-800"
            data-testid={`${resource}-add-btn`}
          >
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
      </div>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    #
                  </th>

                  {fields
                    .filter((field) => field.list !== false)
                    .slice(0, 4)
                    .map((field) => (
                      <th
                        key={field.key}
                        className="text-left px-4 py-3 font-medium text-slate-600"
                      >
                        {field.label}
                      </th>
                    ))}

                  {extraColumns.map((column) => (
                    <th
                      key={column.key}
                      className="text-left px-4 py-3 font-medium text-slate-600"
                    >
                      {column.label}
                    </th>
                  ))}

                  <th className="text-left px-4 py-3 font-medium text-slate-600">
                    Active
                  </th>

                  <th className="text-right px-4 py-3 font-medium text-slate-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading && (
                  <tr>
                    <td
                      colSpan={99}
                      className="text-center py-16 text-slate-400"
                    >
                      Loading…
                    </td>
                  </tr>
                )}

                {isError && (
                  <tr>
                    <td
                      colSpan={99}
                      className="text-center py-16 text-red-500"
                    >
                      Unable to load {title.toLowerCase()}.
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  !isError &&
                  items.map((item, index) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`${resource}-row-${item.id}`}
                    >
                      <td className="px-4 py-3 text-slate-500">
                        {index + 1}
                      </td>

                      {fields
                        .filter((field) => field.list !== false)
                        .slice(0, 4)
                        .map((field) => (
                          <td
                            key={field.key}
                            className="px-4 py-3 text-slate-800"
                          >
                            {field.type === "image" &&
                            item[field.key] ? (
                              <img
                                src={
                                  item[`${field.key}_variants`]
                                    ?.tablet || item[field.key]
                                }
                                className="h-10 w-16 rounded object-cover"
                                alt=""
                              />
                            ) : field.type === "switch" ? (
                              item[field.key] ? (
                                "Yes"
                              ) : (
                                "No"
                              )
                            ) : typeof item[field.key] === "object" ? (
                              JSON.stringify(item[field.key]).slice(
                                0,
                                50
                              )
                            ) : (
                              String(
                                item[field.key] ?? ""
                              ).slice(0, 60)
                            )}
                          </td>
                        ))}

                      {extraColumns.map((column) => (
                        <td
                          key={column.key}
                          className="px-4 py-3 text-slate-800"
                        >
                          {column.render
                            ? column.render(item)
                            : item[column.key]}
                        </td>
                      ))}

                      <td className="px-4 py-3">
                        <Switch
                          checked={!!item.is_active}
                          onCheckedChange={() =>
                            toggle.mutate(item.id)
                          }
                          disabled={toggle.isPending}
                          data-testid={`${resource}-toggle-${item.id}`}
                        />
                      </td>

                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => openEdit(item)}
                            data-testid={`${resource}-edit-${item.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>

                          <Button
                            variant="outline"
                            size="icon"
                            disabled={del.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Delete this item?"
                                )
                              ) {
                                del.mutate(item.id);
                              }
                            }}
                            data-testid={`${resource}-delete-${item.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!isLoading &&
                  !isError &&
                  items.length === 0 && (
                    <tr>
                      <td
                        colSpan={99}
                        className="text-center py-16 text-slate-400"
                      >
                        No items yet.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ===================================================
          CREATE / EDIT POPUP
      =================================================== */}

      <Dialog
        open={creating || !!editing}
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Edit" : "Create"}{" "}
              {title.replace(/s$/, "")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            {fields.map((field) => (
              <div key={field.key}>
                <Label>{field.label}</Label>

                {/* TEXTAREA */}
                {field.type === "textarea" ? (
                  <Textarea
                    rows={field.rows || 3}
                    value={form[field.key] || ""}
                    onChange={(e) =>
                      setField(field.key, e.target.value)
                    }
                    data-testid={`field-${field.key}`}
                  />
                ) : field.type === "number" ? (
                  /* NUMBER */
                  <Input
                    type="number"
                    value={form[field.key] ?? ""}
                    onChange={(e) =>
                      setField(
                        field.key,
                        e.target.value === ""
                          ? ""
                          : Number(e.target.value)
                      )
                    }
                    data-testid={`field-${field.key}`}
                  />
                ) : field.type === "switch" ? (
                  /* SWITCH */
                  <div className="pt-2">
                    <Switch
                      checked={!!form[field.key]}
                      onCheckedChange={(value) =>
                        setField(field.key, value)
                      }
                      data-testid={`field-${field.key}`}
                    />
                  </div>
                ) : field.type === "image" ? (
                  /* IMAGE - now also captures responsive variants
                     into the sibling `${field.key}_variants` key */
                  <ImageUpload
                    value={form[field.key]}
                    onChange={(value) =>
                      setField(field.key, value)
                    }
                    variants={form[`${field.key}_variants`]}
                    onVariantsChange={(variants) =>
                      setField(`${field.key}_variants`, variants)
                    }
                    testId={`field-${field.key}`}
                  />
                ) : field.type === "select" ? (
                  /* SELECT */
                  <select
                    value={form[field.key] || ""}
                    onChange={(e) =>
                      setField(field.key, e.target.value)
                    }
                    className="w-full h-10 border rounded-md px-3 bg-white border-slate-300"
                    data-testid={`field-${field.key}`}
                  >
                    <option value="">
                      — Select —
                    </option>

                    {(field.options || []).map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "list" ? (
                  /* LIST */
                  <Textarea
                    rows={4}
                    value={
                      Array.isArray(form[field.key])
                        ? form[field.key].join("\n")
                        : form[field.key] || ""
                    }
                    onChange={(e) =>
                      setField(
                        field.key,
                        e.target.value
                          .split("\n")
                          .map((item) => item.trim())
                          .filter(Boolean)
                      )
                    }
                    placeholder="One item per line"
                    data-testid={`field-${field.key}`}
                  />
                ) : field.type === "json" ? (
                  /* JSON */
                  <Textarea
                    rows={field.rows || 6}
                    value={
                      typeof form[field.key] === "string"
                        ? form[field.key]
                        : JSON.stringify(
                            form[field.key] || [],
                            null,
                            2
                          )
                    }
                    onChange={(e) =>
                      setField(
                        field.key,
                        e.target.value
                      )
                    }
                    placeholder="JSON"
                    className="font-mono text-xs"
                    data-testid={`field-${field.key}`}
                  />
                ) : (
                  /* TEXT */
                  <Input
                    type={field.type || "text"}
                    value={form[field.key] || ""}
                    onChange={(e) =>
                      setField(
                        field.key,
                        e.target.value
                      )
                    }
                    data-testid={`field-${field.key}`}
                  />
                )}

                {field.hint && (
                  <div className="text-xs text-slate-500 mt-1">
                    {field.hint}
                  </div>
                )}
              </div>
            ))}

            {/* =================================================
                BUTTONS
            ================================================= */}

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={close}
                disabled={save.isPending}
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={() => save.mutate()}
                disabled={save.isPending}
                className="bg-slate-950 hover:bg-slate-800"
                data-testid={`${resource}-save-btn`}
              >
                {save.isPending
                  ? "Saving…"
                  : editing
                  ? "Update"
                  : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* =========================================================
   EXPORT IMAGE UPLOAD
========================================================= */

export { ImageUpload };